import { Link, useSegments, useRouter } from "expo-router";
import { useMemo, useRef, useState } from "react";
import { View, Platform, Pressable, ScrollView } from "react-native";
import { Image, type ImageSource } from "expo-image";
import { ThemedText } from "@/components/ThemedText";
import { MaterialIcons } from "@expo/vector-icons";
import { IconSymbol } from "@/components/ui/IconSymbol";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { titleCase } from "@/lib/helpers";
import { CallToggle } from "@/components/Calls/CallToggle";
import ContextMenu from "@/components/ContextMenu";
import { ENTITY_ICONS, EntityIconKey } from "@/constants/icons";
import { useEntityTablePageConfig } from "@/providers/EntityTablePageProvider";
import { SearchZone } from "@/components/ActionBar/SearchZone";
import { SelectionZone } from "@/components/ActionBar/SelectionZone";

type NavItem = { key: string; label: string; href: string };

function SectionIcon({ section, color, size = 18 }: { section: string; color: string; size?: number }) {
  const iconKey = section === "index" ? "dashboard" : section;
  const iconName = ENTITY_ICONS[iconKey as EntityIconKey];
  if (!iconName) return null;
  return <IconSymbol name={iconName} size={size} color={color} />;
}

export function HeaderLeft() {
  const segs = useSegments();
  const router = useRouter();
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const permissions = usePermissions();
  const [logo] = useState<ImageSource>(require("@/assets/images/rockforce-logo-rf-yellow.svg"));
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef<View>(null);
  const etpConfig = useEntityTablePageConfig();

  const isRoot = segs[1] === "(root)";
  const isSettings = segs[1] === "settings";

  let sectionKey: string | null = null;
  let sectionDisplayName: string | null = null;

  if (isRoot) {
    const raw = segs[2]?.replace("(", "").replace(")", "") ?? null;
    if (raw && raw !== "index") {
      sectionKey = raw;
      sectionDisplayName = displayNames?.[raw]?.plural ?? titleCase(raw.replace("_", " "));
    }
  } else if (isSettings) {
    sectionKey = "settings";
    sectionDisplayName = "Settings";
  }

  const navItems = useMemo(() => {
    const permMap: Record<string, boolean> = {
      projects: permissions.hasProjectsAccess,
      shifts: permissions.hasShiftsAccess,
      chat: permissions.hasChatAccess,
      users: permissions.hasUsersAccess,
      contacts: permissions.hasContactsAccess,
      invoices: permissions.hasInvoicesAccess,
      estimates: permissions.hasEstimatesAccess,
      time_cards: permissions.hasTimeCardsAccess,
      expenses: permissions.hasExpensesAccess,
      courses: permissions.hasCoursesAccess,
      incidents: permissions.hasIncidentsAccess,
      investigations: permissions.hasInvestigationsAccess,
      payroll: permissions.hasPayrollAccess,
      applications: permissions.hasApplicationsAccess,
      clients: permissions.hasClientsAccess,
    };

    const items: NavItem[] = [{ key: "index", label: "Dashboard", href: "/" }];

    for (const [key, hasAccess] of Object.entries(permMap)) {
      if (!hasAccess) continue;
      const label = displayNames?.[key]?.plural ?? titleCase(key.replace("_", " "));
      items.push({ key, label, href: `/${key}` });
    }

    items.push({ key: "settings", label: "Settings", href: "/settings" });

    return items;
  }, [permissions, displayNames]);

  return (
    <View
      style={{
        flex: 1,
        height: "100%",
        gap: 8,
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
      }}
    >
      <Link asChild href={"/"}>
        <Image source={[logo]} style={{ height: 34, width: 34, marginLeft: 12 }} />
      </Link>
      <Link asChild href={"/"}>
        {/* <ThemedText style={{ fontWeight: 700, fontSize: 18 }}>NEXUS</ThemedText> */}
      </Link>
      {sectionKey && sectionDisplayName && (
        <>
          <MaterialIcons name="chevron-right" color={colors.text} />
          <Pressable
            ref={triggerRef}
            onPress={() => setMenuOpen((v) => !v)}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              paddingVertical: 4,
              paddingHorizontal: 8,
              borderRadius: 8,
            }}
          >
            <SectionIcon section={sectionKey} color={colors.text} />
            <ThemedText style={{ fontWeight: 400, fontSize: 18 }}>{sectionDisplayName}</ThemedText>
            <MaterialIcons
              name={menuOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
              size={18}
              color={colors.textSecondary}
            />
          </Pressable>
          <ContextMenu
            visible={menuOpen}
            setVisible={setMenuOpen}
            ignoreRef={triggerRef}
            position={{ top: 38, left: 90 }}
            style={{ minWidth: 220, maxHeight: 420, padding: 4 }}
          >
            <ScrollView>
              {navItems.map((item) => {
                const isActive = item.key === sectionKey;
                return (
                  <Pressable
                    key={item.key}
                    onPress={() => {
                      setMenuOpen(false);
                      router.push(item.href as any);
                    }}
                    style={({ hovered }: any) => ({
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 10,
                      paddingVertical: 8,
                      paddingHorizontal: 10,
                      borderRadius: 6,
                      backgroundColor: isActive
                        ? colors.backgroundHover
                        : hovered
                          ? colors.backgroundHover
                          : "transparent",
                    })}
                  >
                    <SectionIcon
                      section={item.key}
                      color={isActive ? colors.primary : colors.textSecondary}
                      size={16}
                    />
                    <ThemedText
                      style={{
                        fontSize: 14,
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? colors.primary : colors.text,
                      }}
                    >
                      {item.label}
                    </ThemedText>
                  </Pressable>
                );
              })}
            </ScrollView>
          </ContextMenu>
        </>
      )}
      {Platform.OS !== "web" && <CallToggle />}

      {/* EntityTablePage left-side controls (driven by context) */}
      {etpConfig && (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6, marginLeft: 8 }}>
          {etpConfig.toggleNode}
          {etpConfig.search && <SearchZone config={etpConfig.search} />}
          {etpConfig.selection && <SelectionZone config={etpConfig.selection} />}
        </View>
      )}
    </View>
  );
}
